import ListCard from '@Components/ListCard/ListCard';
import AccessoryModal from '@Components/Modals/Accessories/AccessoryModal';
import DeleteAccessoryModal from '@Components/Modals/Accessories/DeleteAccessoryModal';
import AccessoriesServices from '@Services/Accessories.services.ts';
import { Accessory } from '@Types/Asset.types';
import { PaginatedResponse } from '@Types/Response.types';
import { PAGE_PROP, PAGE_SIZE, PAGINATED_DEFAULT, PATHS, SEARCH_PROP, TABLE_STRUCTURES } from '@Utils/constants';
import { handleRequestError } from '@Utils/request.utils';
import { Button, Icons, Pagination, SearchBox, SectionCard, useTopHat } from '@kg-internal/pv3-uikit';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

const Accessories = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [accessories, setAccessories] = useState<PaginatedResponse<Accessory>>(PAGINATED_DEFAULT);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string>();
  const currentPage = Number(searchParams.get(PAGE_PROP) ?? '1');
  const searchQuery = searchParams.get(SEARCH_PROP) ?? '';
  const [showAccessoryModal, setShowAccessoryModal] = useState<boolean>(false);
  const [showDeleteAccessoryModal, setShowDeleteAccessoryModal] = useState<boolean>(false);
  const [selectedAccessory, setSelectedAccessory] = useState<Accessory | undefined>();

  useTopHat({
    activeNav: 'nav-settings',
    activeTab: undefined,
    tabs: [],
    breadcrumbs: [
      { title: 'Settings', href: PATHS.SETTINGS, id: 'Settings' },
      {
        title: 'Accessories',
        href: `${PATHS.ACCESSORY_SETTINGS}?page=${1}`,
        id: 'Accessories',
      },
    ],
  });

  const handleNewPage = (newPage: number, data: PaginatedResponse<Accessory>) => {
    if (newPage <= 0 || newPage > accessories.totalPages) {
      return;
    }
    const page = newPage > data.totalPages ? data.prevPage : newPage;
    if (page !== currentPage) {
      searchParams.set(PAGE_PROP, page?.toString() || '1');
      setSearchParams(searchParams);
    }
  };

  const getAccessories = () => {
    setSelectedAccessory(undefined);
    AccessoriesServices.searchAccessories({ search: searchQuery }, currentPage)
      .then(({ data }) => {
        setAccessories(data);
        setErrorMessage(undefined);
        handleNewPage(currentPage, data);
      })
      .catch((error) => {
        handleRequestError(error, setErrorMessage);
      })
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    setIsLoading(true);
    getAccessories();
  }, [searchQuery, currentPage]);

  const handleSearchQueryChange = (newValue: string) => {
    searchParams.set(SEARCH_PROP, newValue);
    searchParams.set(PAGE_PROP, '1');
    if (!newValue) {
      searchParams.delete(SEARCH_PROP);
    }
    setSearchParams(searchParams);
  };

  const handleAccessoryAction = (id: string, setModal: (show: boolean) => void) => {
    setSelectedAccessory(accessories.docs.find((asset: Accessory) => asset.id === id));
    setModal(true);
  };

  return (
    <div className='grid'>
      <div className='col-12 col-md-3'>
        <SectionCard>
          <SearchBox
            id='search-accessories'
            fullWidth
            focusOnRender
            value={searchQuery}
            defaultValue={searchQuery}
            onChange={handleSearchQueryChange}
          />
          <Button type='primary' className='button-with-icon' fullWidth onClick={() => setShowAccessoryModal(true)}>
            {Icons.Plus} Add new Accessory
          </Button>
        </SectionCard>
      </div>
      <div className='col-12 col-md-9'>
        <ListCard<Accessory>
          title="Accessories"
          structure={TABLE_STRUCTURES.ACCESSORY}
          data={accessories.docs}
          loading={isLoading}
          withActions={{
            edit: (editId) => handleAccessoryAction(editId, setShowAccessoryModal),
            delete: (deleteId) => handleAccessoryAction(deleteId, setShowDeleteAccessoryModal),
          }}
          error={errorMessage}
          searchEntered={!!searchQuery}
        />
      </div>
      <div className='col-12 content-middle content-right-md'>
        <Pagination
          count={accessories.totalDocs}
          perPage={PAGE_SIZE}
          currentPage={currentPage - 1}
          onChange={(page) => handleNewPage(page + 1, accessories)}
        />
      </div>
      <AccessoryModal
        show={showAccessoryModal}
        accessory={selectedAccessory}
        onClose={() => {
          setShowAccessoryModal(false);
          setSelectedAccessory(undefined);
        }}
        onSuccess={() => getAccessories()}
      />
      <DeleteAccessoryModal
        show={showDeleteAccessoryModal}
        accessory={selectedAccessory}
        onClose={() => {
          setShowDeleteAccessoryModal(false);
          setSelectedAccessory(undefined);
        }}
        onSuccess={() => getAccessories()}
      />
    </div>
  );
};

export default Accessories;
